import * as React from "react";
import { useMemo } from "react";

import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import CircularProgress from "components/circular-progress";
import {
  MY_COMPLETED_TASKS,
  TASKS_ASSIGNED_TO_ME,
  TASKS_CREATED_BY_ME,
  TASKS_TYPE
} from "constants/staff-tasks";
import useLoadMoreByPriority from "features/staff-tasks-v2/hooks/use-load-more-by-priority.hook";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { getCardPosition } from "features/staff-tasks-v2/staff-task.utils";
import type { StaffTasksGroupByPriorityResponse } from "services/types/staff-tasks.type";

import { getTasksByPriority } from "../../components/staff-task-list-content/utils";
import StaffTaskCard from "../staff-task-card/staff-task-card.component";
import GroupByPriorityContainer from "./group-by-priority-container.component";
import NoTasksPlaceholder from "./no-tasks-placeholder.component";

type TaskTabsProps = {
  taskViewType: TASKS_TYPE;
};

const DEFAULT_GROUP_DATA: StaffTasksGroupByPriorityResponse = {
  totalCount: 0,
  items: {}
};

export default function TasksTab({ taskViewType }: TaskTabsProps) {
  const {
    tasksAssignedToMe,
    tasksCreatedByMe,
    myCompletedTasks,
    isDrawerDataBusy,
    isUpdating,
    handleOpenTaskModal,
    tabBaseFilters
  } = useStaffTasksV2();

  const { data, placeholderText } = useMemo((): {
    data: StaffTasksGroupByPriorityResponse | undefined;
    placeholderText: string;
  } => {
    if (taskViewType === TASKS_ASSIGNED_TO_ME) {
      return {
        data: tasksAssignedToMe,
        placeholderText: "No Assigned to Me Tasks Available"
      };
    } else if (taskViewType === TASKS_CREATED_BY_ME) {
      return {
        data: tasksCreatedByMe,
        placeholderText: "No Created By Me Tasks Available"
      };
    } else if (taskViewType === MY_COMPLETED_TASKS) {
      return {
        data: myCompletedTasks,
        placeholderText: "No Completed Tasks Available"
      };
    }

    return { data: undefined, placeholderText: "" };
  }, [myCompletedTasks, taskViewType, tasksAssignedToMe, tasksCreatedByMe]);

  const baseFilters = tabBaseFilters[taskViewType];

  const { mergedData, getGroupLoadMoreProps } = useLoadMoreByPriority(
    data || DEFAULT_GROUP_DATA,
    baseFilters
  );

  const groups = useMemo(
    () => (mergedData ? getTasksByPriority(mergedData) : []),
    [mergedData]
  );

  const noData = !groups.some((group) => group.data.length);

  return (
    <Box
      width="100%"
      sx={{
        overflow: "hidden",
        boxSizing: "border-box",
        mt: 2,
        mb: 4
      }}
    >
      {(isDrawerDataBusy || isUpdating) && (
        <Box
          sx={{
            position: "absolute",
            right: 24,
            mt: -1.5,
            opacity: noData || isUpdating ? 0.5 : 0.25,
            background: grey[100],
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            p: 1,
            transform: "scale(0.8)"
          }}
        >
          <CircularProgress sx={{ py: 0 }} />
        </Box>
      )}

      {noData && !isDrawerDataBusy && !isUpdating && (
        <NoTasksPlaceholder
          label={placeholderText}
          onCreateNew={() => {
            handleOpenTaskModal({ mode: "create" });
          }}
        />
      )}
      {!noData &&
        groups.length > 0 &&
        groups.map(({ data, priority, count }) =>
          data.length > 0 ? (
            <GroupByPriorityContainer
              key={priority}
              priority={priority}
              count={count}
              {...getGroupLoadMoreProps(priority, data.length, count)}
            >
              {data.map((task, index) => (
                <StaffTaskCard
                  key={task.id}
                  task={task}
                  position={getCardPosition(index, data.length)}
                />
              ))}
            </GroupByPriorityContainer>
          ) : null
        )}
    </Box>
  );
}
